import React, { memo, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import SwiperCore, { Pagination, Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  AppFunctionComponent,
  RenderRichText,
  TextWithEmbedBuilder,
  TextWithEmbedLayout,
} from "../../../types"
import "swiper/swiper-bundle.min.css"
import { ScrollAnchor } from "../sections/custom/innovation-lab-form/innovation-lab.component"
import { SectionContainer } from "../../../resolvers/text/text.resolver"
import {
  HeroHeading,
  HeroImage,
  HeroText,
} from "../sections/hero-section-refactored.component"
import { SectionHeading } from "../sections/section.component"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import TextWithEmbedResolver from "../../../resolvers/text-with-embed/text-with-embed.resolver"
import { SliderButtonStyle } from "../sections/custom/quick-start/quick-start.styled"
import useTranslationHook from "../../../hooks/use-translation.hook"

export const ArrowContainer = styled.div<{ active: boolean }>`
  top: 50%;
  opacity: 1;
  width: 42px;
  height: 42px;
  display: flex;
  cursor: pointer;
  position: absolute;
  border-radius: 50%;
  align-items: center;
  flex-direction: row;
  pointer-events: all;
  visibility: visible;
  user-selectable: none;
  justify-content: center;
  transform: translateY(-50%);
  background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  z-index: 1000;
  .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 0 4px;
    cursor: pointer;
    background-color: ${({ theme }) => theme.color.dot.inactive};
    opacity: 1;
    &:hover {
      background-color: ${({ theme }) => theme.color.dot.hover};
    }
  }

  .swiper-pagination-bullet-active {
    background-color: ${({ theme }) => theme.color.dot.active};
  }

  &.swiper-button-disabled {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    display: none;
  }

  ${({ active }) =>
    !active &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`

export const ArrowPrev = styled(ArrowContainer)`
  left: 5%;

  svg {
    transform: rotateZ(180deg);
  }
`

export const ArrowNext = styled(ArrowContainer)`
  right: 5%;
`

export const ControlsPrevIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M13.2929 6.05078L7.58594 11.7578L13.2929 17.4648L14.7069 16.0508L10.4139 11.7578L14.7069 7.46478L13.2929 6.05078Z"
      fill="black"
    />
  </svg>
)

export const ControlsNextIcon: AppFunctionComponent = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M9.70697 17.707L15.414 12L9.70697 6.29297L8.29297 7.70697L12.586 12L8.29297 16.293L9.70697 17.707Z"
      fill="black"
    />
  </svg>
)

const SwiperWrapper = styled(Swiper)`
  width: 100%;
`

interface Props {
  slides: TextWithEmbedBuilder[]
  anchorId?: string
  isArrowsDisabled?: boolean
  text?: RenderRichText
}

SwiperCore.use([Pagination, Navigation])

const TextWithEmbedCarouselStock: AppFunctionComponent<Props> = memo(
  ({ slides, text, anchorId }) => {
    const [swiper, setSwiper] = useState<SwiperCore>()
    const [activeIndex, setActiveIndex] = useState(0)
    const ref = useRef<HTMLDivElement | null>(null)
    const { dynamicTranslate } = useTranslationHook()

    useEffect(() => {
      if (swiper) {
        setActiveIndex(swiper.activeIndex)
      }
    }, [swiper?.activeIndex])

    return (
      <SliderContainer>
        {text && (
          <StyledSectionText>
            <SectionHeading>{renderRichText(text)}</SectionHeading>
          </StyledSectionText>
        )}
        <StyledSwiperWrapper
          navigation={{
            nextEl: ".swiper-arrow-right-stock",
            prevEl: ".swiper-arrow-left-stock",
          }}
          onSwiper={(swiper) => setSwiper(swiper)}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
          }}
          pagination={{ el: ".swiper-pagination-stock", clickable: true }}
          data-testid="hero-carousel"
          ref={ref}
        >
          <ScrollAnchor id={anchorId} />
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              <TextWithEmbedResolver
                {...slide}
                layout={TextWithEmbedLayout.LeftText}
              />
            </SwiperSlide>
          ))}
        </StyledSwiperWrapper>
        <StyledSwiperPagination className="swiper-pagination-stock"></StyledSwiperPagination>
        {/* <StyledControlsButtonWrapper>
        <SliderButtonStyleVanishing
          className="swiper-arrow-left-stock"
          disabledPrev={activeIndex === 0}
          onClick={() => {
            const objectPosition = ref.current?.getBoundingClientRect()
            window.scrollTo(
              objectPosition!.left,
              objectPosition!.top + window.scrollY - 60
            )
          }}
        >
          <ControlsPrevIcon />
          {dynamicTranslate("PREV")}
        </SliderButtonStyleVanishing>
        <SliderButtonStyleVanishing
          className="swiper-arrow-right-stock"
          disabledNext={activeIndex === slides.length - 1}
          onClick={() => {
            const objectPosition = ref.current?.getBoundingClientRect()
            window.scrollTo(
              objectPosition!.left,
              objectPosition!.top + window.scrollY - 60
            )
          }}
        >
          {dynamicTranslate("NEXT")}
          <ControlsNextIcon />
        </SliderButtonStyleVanishing>
      </StyledControlsButtonWrapper> */}
      </SliderContainer>
    )
  }
)

export default TextWithEmbedCarouselStock

const SliderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    overflow: hidden;
  }
`

const StyledSwiperWrapper = styled(SwiperWrapper)`
  /* margin-top: 2rem; */

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    /* margin-top: 8rem; */
  }

  section {
    align-items: center;
    height: unset;
    max-height: unset;
  }
  ${HeroImage} {
    border-radius: 8px;
    border: 2px solid #cdcdcd;
    padding: 2rem;
    img {
      top: 5%;
      width: 100%;
      height: 120%;
      object-fit: contain;
      object-position: top;
    }
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    }
  }
  ${HeroHeading} {
    h1 {
      text-align: left;
    }
  }
  ${HeroText} {
    p {
      text-align: left;
    }
  }
`

const StyledSwiperPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  .swiper-pagination-bullet {
    cursor: pointer;
  }

  .swiper-pagination-bullet-active {
    background: #000;
  }
`

const StyledSectionText = styled(SectionContainer)`
  width: 100%;
  padding: 0 20px;
  ${SectionHeading} {
    grid-area: unset;
    h1 {
      color: #000;
      text-align: center;
      font-family: Facto;
      font-size: 46px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
    }
    p {
      color: #000;
      text-align: center;
      font-family: Montserrat;
      font-size: 18px;
      font-style: normal;
      font-weight: 300;
      line-height: 150%;
    }
    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      h1 {
        color: #000;
        font-family: Facto;
        font-size: 30px;
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
        text-align: left;
      }
      p {
        color: #000;
        font-family: Montserrat;
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 150%;
        text-align: left;
      }
    }
  }
`

export const StyledControlsButtonWrapper = styled.div`
  display: flex;
  gap: 2rem;
  width: 100%;
  max-width: 1260px;
  margin: auto;
  position: relative;
  top: -15%;
  z-index: 3;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: flex;
    justify-content: space-between;
    width: calc(100% - 40px);
    top: unset;
    bottom: 0;
  }

  button {
    cursor: pointer;
    padding: 10px 20px;

    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      min-width: unset;
    }
  }
`

export const TestimonialsControlsWrapper = styled(StyledControlsButtonWrapper)`
  button {
    height: 50px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    width: calc(100% - 40px);
    position: absolute;
    top: 53%;
    justify-content: space-between;
    z-index: 10;
  }
`

export const SliderButtonStyleVanishing = styled(SliderButtonStyle)<{
  disabledNext?: boolean
  disabledPrev?: boolean
}>`
  ${({ disabledPrev, disabledNext }) =>
    (disabledPrev || disabledNext) &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        border: 2px solid ${({ theme }) => theme.color.placeholder};
        color: ${({ theme }) => theme.color.placeholder};
        opacity: 1;
        svg {
          path {
            fill: ${({ theme }) => theme.color.placeholder};
          }
        }
      }
    `}
`
